/**
 * Pilot Production authenticated-experience centres
 * -------------------------------------------------
 *  <PilotProductionIdentity />  — official environment identification strip
 *  <AgreementCentre />          — consolidated LOI / NDA / MOU view
 *  <DocumentCentre />           — grouped document view with smart empty states
 *  <NotificationCentre />       — categorised notifications with read actions
 *
 * These components only READ through existing investorService methods; they
 * never simulate completion and never bypass server-side workflow locking.
 * Every empty state explains what will happen next instead of "No records".
 */
import React, { useMemo, useState } from 'react';
import {
  FileSignature, FolderOpen, Bell, CheckCheck, ExternalLink,
  ShieldCheck, Landmark, Filter,
} from 'lucide-react';
import {
  Badge,
  EmptyState,
  Loading,
} from '@/components/eds';
import { safeUrl } from '@/lib/urlSafety';

import {
  investorService,
  type AgreementRecord,
  type PortalDocument,
  type NotificationItem,
  type WorkflowStage,
  deriveWorkflowStages,
  type InvestorProfileSummary,
} from '@/services/investorService';

/* ───────────────── shared primitives ───────────────── */

/**
 * Accessible EDS-backed status badge.
 * Existing local tone contract is preserved so callers remain unchanged.
 */
const StatusBadge: React.FC<{
  label: string;
  tone?: 'ok' | 'warn' | 'muted' | 'info';
}> = ({ label, tone = 'muted' }) => {
  const edsTone =
    tone === 'ok'
      ? 'success'
      : tone === 'warn'
        ? 'warning'
        : tone === 'info'
          ? 'info'
          : 'neutral';

  return (
    <Badge tone={edsTone} dot>
      {label}
    </Badge>
  );
};

const toneFor = (status?: string): 'ok' | 'warn' | 'muted' | 'info' => {
  const s = (status || '').toLowerCase();
  if (['signed', 'approved', 'active', 'completed', 'verified', 'published', 'paid'].some((k) => s.includes(k))) return 'ok';
  if (['pending', 'awaiting', 'attention', 'rejected', 'overdue'].some((k) => s.includes(k))) return 'warn';
  if (['review', 'submitted', 'in_progress', 'in progress'].some((k) => s.includes(k))) return 'info';
  return 'muted';
};

const humanise = (v?: string) =>
  v ? v.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const fmtDate = (v?: string) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

/**
 * Smart EDS empty state — preserves the existing explanatory contract.
 */
const SmartEmpty: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}> = ({ icon: Icon, title, body }) => (
  <EmptyState
    role="status"
    icon={<Icon className="h-5 w-5" />}
    title={title}
    message={body}
  />
);

/* ───────────────── Pilot Production identification ───────────────── */

export const PilotProductionIdentity: React.FC = () => (
  <div
    className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 rounded-eds-md border border-eds-success/30 bg-eds-success/10"
    role="note"
    aria-label="Environment identification"
  >
    <Landmark className="w-4 h-4 text-eds-success shrink-0" aria-hidden="true" />
    <span className="text-xs font-semibold text-eds-fg">
      GIISDEC Geological Information &amp; Investment Platform
    </span>
    <span className="text-xs text-eds-fg">Pilot Production Environment</span>
    <span className="hidden sm:inline text-eds-success/40" aria-hidden="true">•</span>
    <span className="text-xs text-eds-fg">Controlled Operations</span>
    <span className="hidden sm:inline text-eds-success/40" aria-hidden="true">•</span>
    <span className="text-xs text-eds-fg">Validation Active</span>
  </div>
);

/* ───────────────── Agreement Centre ───────────────── */

const AGREEMENT_LABELS: Record<string, string> = {
  LOI: 'Letter of Intent',
  NDA: 'Non-Disclosure Agreement',
  MOU: 'Memorandum of Understanding',
};

interface AgreementCentreProps {
  agreements: AgreementRecord[];
  loading?: boolean;
  workflow: WorkflowStage[];
  profile: InvestorProfileSummary | null;
}

/** Consolidated LOI / NDA / MOU view. Uses server agreement records when
 * present; otherwise derives per-type availability from the governed workflow
 * so locked stages are clearly explained (never simulated as complete). */
export const AgreementCentre: React.FC<AgreementCentreProps> = ({ agreements, loading, workflow, profile }) => {
  const stages = useMemo(() => deriveWorkflowStages(workflow, profile), [workflow, profile]);
  const stageByType: Record<string, WorkflowStage | undefined> = {
    LOI: stages.find((s) => s.key === 'loi'),
    NDA: stages.find((s) => s.key === 'nda'),
    MOU: stages.find((s) => s.key === 'mou'),
  };

  if (loading) {
    return (
      <Loading
        label="Loading agreements…"
        className="py-4"
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FileSignature className="w-4 h-4 text-eds-success" aria-hidden="true" />
        <h3 className="font-semibold text-eds-fg text-sm">Agreement Centre</h3>
        <span className="text-sm font-medium text-eds-muted-fg">LOI · NDA · MOU — one consolidated view</span>
      </div>

      {agreements.length > 0 ? (
        <div className="overflow-x-auto border border-eds-border rounded-eds-md">
          <table className="w-full text-sm min-w-[720px]">
            <caption className="sr-only">Your agreements and their signature status</caption>
            <thead className="text-left text-xs uppercase text-eds-muted-fg bg-eds-surface-2">
              <tr>
                <th scope="col" className="py-2.5 px-3">Type</th>
                <th scope="col" className="px-3">Title / Reference</th>
                <th scope="col" className="px-3">Status</th>
                <th scope="col" className="px-3">Investor signature</th>
                <th scope="col" className="px-3">Authority signature</th>
                <th scope="col" className="px-3">Effective</th>
                <th scope="col" className="px-3">Updated</th>
                <th scope="col" className="px-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {agreements.map((a) => (
                <tr key={a.id} className="border-t border-eds-border">
                  <td className="py-2.5 px-3 font-medium text-eds-fg">
                    {AGREEMENT_LABELS[String(a.type).toUpperCase()] ?? humanise(String(a.type))}
                  </td>
                  <td className="px-3 text-eds-fg">
                    <div>{a.title || '—'}</div>
                    {a.reference && <div className="text-xs text-eds-muted-fg font-mono">{a.reference}</div>}
                  </td>
                  <td className="px-3"><StatusBadge label={humanise(a.status)} tone={toneFor(a.status)} /></td>
                  <td className="px-3"><StatusBadge label={humanise(a.investorSignature)} tone={toneFor(a.investorSignature)} /></td>
                  <td className="px-3"><StatusBadge label={humanise(a.authoritySignature)} tone={toneFor(a.authoritySignature)} /></td>
                  <td className="px-3 text-eds-muted-fg">{fmtDate(a.effectiveDate)}</td>
                  <td className="px-3 text-eds-muted-fg">{fmtDate(a.updatedAt)}</td>
                  <td className="px-3">
                    {safeUrl(a.documentUrl) ? (
                      <a
                        href={safeUrl(a.documentUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-eds-success hover:opacity-80 text-xs font-medium rounded-eds-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
                      >
                        View <ExternalLink className="w-3 h-3" aria-hidden="true" />
                        <span className="sr-only">(opens in a new tab)</span>
                      </a>
                    ) : (
                      <span className="text-xs text-eds-muted-fg">{a.availableAction ? humanise(a.availableAction) : 'No action required'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <SmartEmpty
          icon={FileSignature}
          title="No agreement is available yet."
          body="Your next agreement will become available when the required preceding stage is completed. The official sequence is LOI → NDA → Payment Validation → MOU."
        />
      )}

      {/* Per-type stage guidance derived from the governed workflow */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(['LOI', 'NDA', 'MOU'] as const).map((t) => {
          const st = stageByType[t];
          const status = st?.status ?? 'locked';
          return (
            <div key={t} className="border border-eds-border rounded-eds-md p-3 bg-eds-surface-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-eds-fg">{AGREEMENT_LABELS[t]}</span>
                <StatusBadge
                  label={humanise(status)}
                  tone={status === 'approved' ? 'ok' : status === 'locked' ? 'muted' : status === 'rejected' ? 'warn' : 'info'}
                />
              </div>
              <p className="text-xs text-eds-muted-fg mt-1.5">
                {status === 'locked'
                  ? 'Becomes available when the preceding workflow stage is completed.'
                  : status === 'approved'
                    ? 'Completed. A signed copy is retained in your Document Centre.'
                    : 'Action may be required — check “Your Next Action” on the overview.'}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ───────────────── Document Centre ───────────────── */

const DOC_GROUPS: { key: string; label: string; match: (c: string) => boolean }[] = [
  { key: 'investor', label: 'Investor Documents', match: (c) => c.includes('investor') },
  { key: 'kyc', label: 'KYC Documents', match: (c) => c.includes('kyc') },
  { key: 'agreement', label: 'Agreements', match: (c) => c.includes('agreement') || ['loi', 'nda', 'mou'].some((k) => c.includes(k)) },
  { key: 'invoice', label: 'Invoices', match: (c) => c.includes('invoice') },
  { key: 'receipt', label: 'Receipts', match: (c) => c.includes('receipt') },
  { key: 'project', label: 'Project Documents', match: (c) => c.includes('project') },
  { key: 'gis_request', label: 'GIS Requests', match: (c) => c.includes('gis') },
  { key: 'correspondence', label: 'Correspondence', match: (c) => c.includes('correspond') },
];

export const DocumentCentre: React.FC<{ documents: PortalDocument[]; loading?: boolean }> = ({ documents, loading }) => {
  const grouped = useMemo(() => {
    const map = new Map<string, PortalDocument[]>();
    for (const d of documents) {
      const cat = String(d.category ?? '').toLowerCase();
      const group = DOC_GROUPS.find((g) => g.match(cat))?.key ?? 'investor';
      map.set(group, [...(map.get(group) ?? []), d]);
    }
    return map;
  }, [documents]);

  if (loading) {
    return (
      <Loading
        label="Loading documents…"
        className="py-4"
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FolderOpen className="w-4 h-4 text-eds-success" aria-hidden="true" />
        <h3 className="font-semibold text-eds-fg text-sm">Document Centre</h3>
        <span className="text-sm font-medium text-eds-muted-fg">All documents, grouped by category</span>
      </div>

      {documents.length === 0 ? (
        <SmartEmpty
          icon={FolderOpen}
          title="No documents are available yet."
          body="Documents appear here as your onboarding progresses — signed agreements, KYC uploads, invoices, and receipts are all retained in this centre. Access permissions are enforced by GIISDEC."
        />
      ) : (
        DOC_GROUPS.filter((g) => (grouped.get(g.key) ?? []).length > 0).map((g) => (
          <section key={g.key} aria-labelledby={`docgrp-${g.key}`}>
            <h4 id={`docgrp-${g.key}`} className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg mb-2">
              {g.label}
            </h4>
            <ul className="divide-y divide-eds-border border border-eds-border rounded-eds-md">
              {(grouped.get(g.key) ?? []).map((d) => (
                <li key={d.id} className="p-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                  <span className="text-sm font-medium text-eds-fg flex-1 min-w-[160px]">{d.name}</span>
                  <StatusBadge label={humanise(d.status)} tone={toneFor(d.status)} />
                  {d.verificationStatus && (
                    <span className="inline-flex items-center gap-1 text-xs text-eds-muted-fg">
                      <ShieldCheck className="w-3.5 h-3.5 text-eds-success" aria-hidden="true" />
                      {humanise(d.verificationStatus)}
                    </span>
                  )}
                  <span className="text-xs text-eds-muted-fg">
                    {fmtDate(d.uploadedAt)}{d.uploadedBy ? ` · ${d.uploadedBy}` : ''}
                  </span>
                  {safeUrl(d.url) ? (
                    <a
                      href={safeUrl(d.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-eds-success hover:opacity-80 text-xs font-medium rounded-eds-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
                    >
                      View <ExternalLink className="w-3 h-3" aria-hidden="true" />
                      <span className="sr-only">(opens in a new tab)</span>
                    </a>
                  ) : (
                    <span className="text-xs text-eds-muted-fg">Access controlled</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
};

/* ───────────────── Notification Centre ───────────────── */

const NOTIF_FILTERS: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'workflow', label: 'Workflow' },
  { key: 'agreement', label: 'Agreement' },
  { key: 'payment', label: 'Payment' },
  { key: 'document', label: 'Document' },
  { key: 'gis', label: 'GIS' },
  { key: 'system', label: 'System' },
  { key: 'support', label: 'Support' },
  { key: 'pilot_update', label: 'Programme Update' }, // G1C1: backend key unchanged; no lifecycle label shown
];

export const NotificationCentre: React.FC<{ rows: NotificationItem[] }> = ({ rows }) => {
  const [items, setItems] = useState<NotificationItem[]>(rows);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState<string | null>(null);

  // Keep local state in sync when the parent refetches.
  React.useEffect(() => { setItems(rows); }, [rows]);

  const catOf = (n: NotificationItem) => String(n.category ?? 'system').toLowerCase();
  const visible = filter === 'all' ? items : items.filter((n) => catOf(n) === filter);
  const unread = items.filter((n) => !n.read).length;

  const markRead = async (id: string) => {
    setBusy(id);
    // Optimistic UI; server remains authoritative and failures are silent-safe.
    setItems((xs) => xs.map((n) => (n.id === id ? { ...n, read: true } : n)));
    await investorService.markNotificationRead(id).catch(() => undefined);
    setBusy(null);
  };

  const markAll = async () => {
    setBusy('__all__');
    setItems((xs) => xs.map((n) => ({ ...n, read: true })));
    await investorService.markAllNotificationsRead().catch(() => undefined);
    setBusy(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Bell className="w-4 h-4 text-eds-success" aria-hidden="true" />
        <h3 className="font-semibold text-eds-fg text-sm">Notification Centre</h3>
        {unread > 0 && <StatusBadge label={`${unread} unread`} tone="info" />}
        <button
          type="button"
          onClick={markAll}
          disabled={unread === 0 || busy === '__all__'}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-eds-sm border border-eds-border text-eds-fg hover:bg-eds-surface-2 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
        >
          <CheckCheck className="w-3.5 h-3.5" aria-hidden="true" /> Mark all as read
        </button>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter notifications by category">
        <Filter className="w-3.5 h-3.5 text-eds-muted-fg" aria-hidden="true" />
        {NOTIF_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            aria-pressed={filter === f.key}
            className={`px-2.5 py-1 text-xs rounded-eds-pill border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring ${
              filter === f.key
                ? 'bg-eds-success text-white border-eds-success'
                : 'bg-eds-surface text-eds-muted-fg border-eds-border hover:bg-eds-surface-2'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <SmartEmpty
          icon={Bell}
          title={filter === 'all' ? 'You are up to date.' : `No ${humanise(filter)} notifications.`}
          body="Workflow progress, agreement updates, payment confirmations, and GIS access decisions will appear here as they occur."
        />
      ) : (
        <ul className="divide-y divide-eds-border border border-eds-border rounded-eds-md">
          {visible.map((n) => (
            <li key={n.id} className={`p-3 ${n.read ? '' : 'bg-eds-success/10'}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-eds-fg text-sm">{n.title}</span>
                <StatusBadge label={humanise(catOf(n))} tone="muted" />
                {!n.read && <StatusBadge label="Unread" tone="info" />}
                <span className="ml-auto text-xs text-eds-muted-fg">{new Date(n.createdAt).toLocaleString()}</span>
              </div>
              <p className="text-sm text-eds-muted-fg mt-0.5">{n.body}</p>
              {!n.read && (
                <button
                  type="button"
                  onClick={() => markRead(n.id)}
                  disabled={busy === n.id}
                  className="mt-1.5 text-xs font-medium text-eds-success hover:opacity-80 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring rounded-eds-sm"
                >
                  Mark as read
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default {
  PilotProductionIdentity,
  AgreementCentre,
  DocumentCentre,
  NotificationCentre,
};
